import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { type Theme, isTheme } from '../theme'

type ThemeDefinition = 'SYSTEM' | 'USER'
type ThemePreference = Theme | null
type SetTheme = (theme: ThemePreference) => void
type ThemeContextValue = readonly [ThemePreference, SetTheme, { definedBy: ThemeDefinition }]

interface ThemeProviderProps {
  children: ReactNode
  initialTheme: ThemePreference
  themeAction: string
}

interface ThemeHeadProps {
  hasServerTheme: boolean
}

interface ThemeMessage {
  preference: ThemePreference
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)
const THEME_CHANNEL_NAME = 'vaults-theme'
const PREFERS_LIGHT_QUERY = '(prefers-color-scheme: light)'
let activePersistenceController: AbortController | null = null

const getSystemTheme = (): Theme => (window.matchMedia(PREFERS_LIGHT_QUERY).matches ? 'light' : 'dark')

const applyTheme = (theme: Theme) => {
  document.documentElement.classList.remove('light', 'dark')
  document.documentElement.classList.add(theme)

  const colorScheme = document.querySelector<HTMLMetaElement>('meta[name="color-scheme"]')
  if (colorScheme) colorScheme.content = theme === 'light' ? 'light dark' : 'dark light'
}

const initialThemeScript = `
(() => {
  const theme = window.matchMedia(${JSON.stringify(PREFERS_LIGHT_QUERY)}).matches ? 'light' : 'dark'
  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(theme)
  const colorScheme = document.querySelector('meta[name="color-scheme"]')
  if (colorScheme) colorScheme.content = theme === 'light' ? 'light dark' : 'dark light'
})()
`

export const ThemeProvider = ({ children, initialTheme, themeAction }: ThemeProviderProps) => {
  const [theme, setTheme] = useState<ThemePreference>(initialTheme)
  const [definedBy, setDefinedBy] = useState<ThemeDefinition>(initialTheme ? 'USER' : 'SYSTEM')
  const channelRef = useRef<BroadcastChannel | null>(null)

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return

    const channel = new BroadcastChannel(THEME_CHANNEL_NAME)
    channelRef.current = channel
    channel.addEventListener('message', (event: MessageEvent<ThemeMessage>) => {
      const preference = event.data?.preference
      if (preference !== null && !isTheme(preference)) return

      activePersistenceController?.abort()
      setDefinedBy(preference ? 'USER' : 'SYSTEM')
      setTheme(preference ?? getSystemTheme())
    })

    return () => {
      channel.close()
      channelRef.current = null
    }
  }, [])

  useEffect(() => {
    if (theme) applyTheme(theme)
  }, [theme])

  useEffect(() => {
    if (definedBy === 'USER') return

    const mediaQuery = window.matchMedia(PREFERS_LIGHT_QUERY)
    setTheme(mediaQuery.matches ? 'light' : 'dark')
    const handleChange = (event: MediaQueryListEvent) => setTheme(event.matches ? 'light' : 'dark')
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [definedBy])

  const updateTheme = useCallback(
    (preference: ThemePreference) => {
      setDefinedBy(preference ? 'USER' : 'SYSTEM')
      setTheme(preference ?? getSystemTheme())
      channelRef.current?.postMessage({ preference } satisfies ThemeMessage)

      activePersistenceController?.abort()
      const controller = new AbortController()
      activePersistenceController = controller

      void fetch(themeAction, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ theme: preference }),
        keepalive: true,
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Theme persistence failed with status ${response.status}`)
        })
        .catch((error: unknown) => {
          if (!(error instanceof DOMException && error.name === 'AbortError')) {
            console.error('Failed to persist theme preference', error)
          }
        })
        .finally(() => {
          if (activePersistenceController === controller) activePersistenceController = null
        })
    },
    [themeAction]
  )

  const value = useMemo(() => [theme, updateTheme, { definedBy }] as const, [definedBy, theme, updateTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used within a ThemeProvider')
  return context
}

export const ThemeHead = ({ hasServerTheme }: ThemeHeadProps) => {
  const [theme] = useTheme()

  return (
    <>
      <meta name="color-scheme" content={theme === 'light' ? 'light dark' : 'dark light'} suppressHydrationWarning />
      {hasServerTheme ? null : (
        // biome-ignore lint/security/noDangerouslySetInnerHtml: This static script must execute before hydration to prevent a wrong-theme flash.
        <script dangerouslySetInnerHTML={{ __html: initialThemeScript }} suppressHydrationWarning />
      )}
    </>
  )
}
